import { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import UploadPanel from '../components/UploadPanel';
import AnnotatePanel from '../components/AnnotatePanel';
import ManagePanel from '../components/ManagePanel';
import useAuthStore from '../store/useAuthStore';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../api/client';

const fetchStats = async () => {
  const response = await apiClient.get('/media/stats');
  return response.data;
};

const StatsPanel = () => {
  const { data: stats, isLoading, isError } = useQuery({
    queryKey: ['stats'],
    queryFn: fetchStats
  });

  if (isLoading) return <div className="panel"><p>Loading statistics...</p></div>;
  if (isError) return <div className="panel"><p className="message error">Failed to load statistics</p></div>;

  return (
    <section className="panel stats-panel">
      <h2>Annotation Statistics</h2>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.totalImages}</div>
          <div className="stat-label">Total Images</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {stats.imagesWithLessThanRequiredAnnotations ?? stats.imagesWithLessThan3}
          </div>
          <div className="stat-label">
            Need More Labels (&lt;{stats.requiredHateAnnotationsPerImage ?? 10})
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {stats.imagesWithAtLeastRequiredAnnotations ?? stats.imagesWithAtLeast3}
          </div>
          <div className="stat-label">
            Sufficiently Labeled (≥{stats.requiredHateAnnotationsPerImage ?? 10})
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.totalAnnotations}</div>
          <div className="stat-label">Total Annotations</div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-value">{stats.userAnnotations}</div>
          <div className="stat-label">Your Annotations</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">
            {(() => {
              const required = stats.requiredHateAnnotationsPerImage ?? 10;
              return stats.totalImages > 0
                ? ((stats.totalAnnotations / (stats.totalImages * required)) * 100).toFixed(1)
                : 0;
            })()}%
          </div>
          <div className="stat-label">Overall Progress</div>
        </div>
      </div>
    </section>
  );
};

const DashboardPage = () => {
  const user = useAuthStore((state) => state.user);
  const setAuth = useAuthStore((state) => state.setAuth);
  const token = useAuthStore((state) => state.token);
  const isAdmin = user?.role === 'admin' || user?.role === 'Admin';
  const isVerified = user?.verificationStatus === 'approved';
  const isPending = user?.verificationStatus === 'pending';
  
  // Set default tab based on user role
  const [activeTab, setActiveTab] = useState(isAdmin ? 'upload' : 'annotate');

  // Update active tab if user role changes
  useEffect(() => {
    if (isAdmin && activeTab === 'annotate') {
      setActiveTab('upload');
    } else if (!isAdmin && (activeTab === 'upload' || activeTab === 'manage')) {
      setActiveTab('annotate');
    }
  }, [isAdmin, activeTab]);

  const handleRequestVerification = async () => {
    try {
      await apiClient.post('/auth/request-verification');
      // Update local user state
      setAuth({ ...user, verificationStatus: 'pending' }, token);
      alert('Verification requested successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to request verification');
    }
  };

  return (
    <div className="dashboard">
      <Navbar activeTab={activeTab} onChangeTab={setActiveTab} />
      <main>
        {activeTab === 'upload' && isAdmin && <UploadPanel />}
        {activeTab === 'manage' && isAdmin && <ManagePanel />}
        {activeTab === 'annotate' && !isAdmin && (
          <>
            {isVerified ? (
              <AnnotatePanel />
            ) : (
              <div className="panel verification-panel" style={{ textAlign: 'center', padding: '40px' }}>
                <h2>Verification Required</h2>
                <p style={{ marginBottom: '20px' }}>You need to be verified by an admin to start annotating.</p>
                
                {isPending ? (
                  <div className="status-message pending" style={{ padding: '15px', background: '#fff3cd', color: '#856404', borderRadius: '4px', display: 'inline-block' }}>
                    <p style={{ margin: 0 }}>⏳ Your verification request is pending approval.</p>
                  </div>
                ) : (
                  <button className="btn primary" onClick={handleRequestVerification}>
                    Apply for Verification
                  </button>
                )}
              </div>
            )}
          </>
        )}
        {activeTab === 'stats' && <StatsPanel />}
      </main>
    </div>
  );
};

export default DashboardPage;
